import type {
	SortCriterionDTO,
	ParameterizedConceptDTORelativeness,
	ConceptDefinitionDTO,
	ParameterizedConceptDTO,
} from "@eu.mdotm.api/smart-workspaces";
import { breakdownDimensionLabel } from "./chain/breakdown";
import type { WorkspaceChain } from "./chain/type";
import type { ParameterizedConceptDTOWithMandatoryUuid } from "./ctx";
import { relativenessWord, takesRelativeness } from "./representation-mode";
import { match } from "ts-pattern";
import { unreachable } from "@mdotm/utils/runtime";

/**
 * Fallback title for a chart with no persisted `label`. The DTO types `label` as optional and the
 * Sphere AI workspace tools can persist a chart without one, so every place that renders or edits a
 * chart title needs the same placeholder.
 */
export const DEFAULT_CHART_LABEL = "Chart";

/**
 * Determines if a concept definition is a metric (numeric with representation modes).
 * Non-metric concepts use the standard column creation path.
 */
export function isMetricConcept(concept: ConceptDefinitionDTO): boolean {
	return (
		concept.dataType === "NUMERIC" && concept.representationModes != null && concept.representationModes.length > 0
	);
}

export function maybeAppendAutogeneratedName<T extends WorkspaceChain>(
	base: T,
	opt?: { ignoreSource?: boolean; autoGenerateName?: boolean },
): T {
	return {
		...base,
		name: !opt?.autoGenerateName ? (base.name ?? "") : autoGenerateMetricName(base, opt),
	};
}

export function autoGenerateMetricName(
	params: { parametrizedConcept: ParameterizedConceptDTO; concept?: ConceptDefinitionDTO },
	opt?: { ignoreSource?: boolean },
): string;
export function autoGenerateMetricName<T extends WorkspaceChain>(base: T, opt?: { ignoreSource?: boolean }): string;
export function autoGenerateMetricName<T extends WorkspaceChain>(
	paramsOrChain: T | { parametrizedConcept: ParameterizedConceptDTO; concept: ConceptDefinitionDTO },
	opt?: { ignoreSource?: boolean },
): string {
	const { concept, parametrizedConcept } =
		"parametrizedConcept" in paramsOrChain && "concept" in paramsOrChain
			? paramsOrChain
			: {
					concept: paramsOrChain.concept,
					parametrizedConcept: {
						source: paramsOrChain.source,
						timeHorizon: paramsOrChain.timeHorizon,
						representationMode: paramsOrChain.representationMode,
						relativeness: paramsOrChain.parameterizedRelativeness,
						parameters: paramsOrChain.parameters,
						breakdown: paramsOrChain.breakdown,
					},
				};
	// While the chain is open the concept is the *fake* tag concept ("Tag"), not the tag the user picked,
	// so a tag breakdown has to find its own name among the tags on offer — otherwise the same chart is
	// called "by Tag" here and "by Sector" once reopened from the persisted concept.
	const breakdownDimension = parametrizedConcept.breakdown?.dimension;
	const breakdownConcept =
		(breakdownDimension && "availableTagsConcepts" in paramsOrChain
			? paramsOrChain.availableTagsConcepts?.find((tag) => tag.breakdownDimensions?.includes(breakdownDimension))
			: undefined) ?? concept;
	// The risk model's pinned levels name the exposure better than the concept does — "Equity / EUR" says
	// more than "Risk Model / Equity / EUR" — so the label is dropped whenever there is a pin to speak for
	// it. A level that was *expanded* leaves no pin behind, and without the label such a chart would be
	// called just "by Currency".
	const parameters = parametrizedConcept.parameters;
	const hasPinnedLevel = hasPinnedRiskModelLevel(parameters);
	return [
		nameDropsConceptLabel(concept, parameters) ? "" : concept?.defaultLabel,
		!opt?.ignoreSource && (concept?.allowedSource?.length ?? 0) > 1
			? match(parametrizedConcept.source)
					.with("CURRENT", () => "Current")
					.with("PROPOSAL", () => "Proposal")
					.otherwise(() => "")
			: "",
		parametrizedConcept.timeHorizon,
		...(parametrizedConcept.representationMode === "ABSOLUTE"
			? []
			: takesRelativeness(parametrizedConcept.representationMode) && parametrizedConcept.relativeness
				? [
						relativenessWord(parametrizedConcept.representationMode),
						parametrizedConcept.relativeness.uuid === undefined
							? createSelfRelativenessLabel(parametrizedConcept.relativeness)
							: parametrizedConcept.relativeness.name,
					]
				: []),
		parametrizedConcept.parameters?.tagExposureOption,
		...(hasPinnedLevel && parameters ? Object.values(parameters) : []),
		// The level that was expanded rather than pinned. Without it a broken-down chart and a chart of the
		// same metric with nothing pinned generate the identical name.
		breakdownDimension ? `by ${breakdownDimensionLabel(breakdownDimension, breakdownConcept)}` : undefined,
	]
		.filter(Boolean)
		.join(" / ");
}

/**
 * Whether any of the risk model's five levels is pinned — an expanded level leaves no pin behind.
 *
 * The cast is the one the chain makes at every other reader of this field: the DTO types `parameters` as
 * a bag of objects, and the five level keys are what the chain actually puts there.
 */
export function hasPinnedRiskModelLevel(
	parameters: WorkspaceChain["parameters"] | ParameterizedConceptDTO["parameters"],
): boolean {
	const levels = parameters as WorkspaceChain["parameters"];
	return Boolean(
		levels?.currency ||
			levels?.geography ||
			levels?.macroAssetClass ||
			levels?.microAssetClass ||
			levels?.microGeography,
	);
}

/**
 * Whether {@link autoGenerateMetricName} lets a pinned level speak for the concept instead of naming it.
 *
 * Exported because the density chart's header draws that name as its own title, and a header that decided
 * this for itself would answer differently the moment the concept's label changed — leaving the title and
 * the name it is supposed to be disagreeing.
 */
export function nameDropsConceptLabel(
	concept: ConceptDefinitionDTO | undefined,
	parameters: WorkspaceChain["parameters"] | ParameterizedConceptDTO["parameters"],
): boolean {
	return concept?.defaultLabel?.toLowerCase() === "risk model" && hasPinnedRiskModelLevel(parameters);
}

/** What the generated name calls a "compare against each portfolio" relativeness — the one with no uuid. */
export function createSelfRelativenessLabel(
	parameterizedConceptDTORelativeness: ParameterizedConceptDTORelativeness,
): string {
	switch (parameterizedConceptDTORelativeness?.mode) {
		case "REFERENCE":
			return "Portfolio's Reference";
		case "BENCHMARK":
			return "Portfolio's Benchmark";
		case "INVESTMENT":
			return "Own portfolio";
		default:
			unreachable(parameterizedConceptDTORelativeness.mode);
	}
}

export function createSortLabel(
	sort: SortCriterionDTO | null,
	parametrizedConcepts: ParameterizedConceptDTOWithMandatoryUuid[],
) {
	if (!sort?.parameterizedConceptUuid) {
		return "None";
	}
	// A sort that names nothing is "None"; a sort that names a concept the pool does not hold was the same
	// answer, and is now impossible — `conceptReferencesResolve` covers `sort` too, and `dropUnresolvableReferences`
	// clears the sort rather than leaving the panel to describe it as unsorted.
	const parameterizedConcept =
		parametrizedConcepts.find((concept) => concept.uuid === sort.parameterizedConceptUuid) ?? unreachable();
	return `${parameterizedConcept.label} - ${sort.direction === "ASC" ? "Ascending" : "Descending"}`;
}
